import React from 'react';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import StyleIcon from '@mui/icons-material/Style';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import SvgIconStyle from '../../components/SvgIconStyle';
import type { SidebarCircle } from '../../features/sidebar-peers/types';
import { isOrgAdmin, type OrganizationMembership } from '../../features/organization-context/types';

const getIcon = (name: string) => (
  <SvgIconStyle src={`/static/icons/navbar/${name}.svg`} sx={{ width: '100%', height: '100%' }} />
);

const ICONS = {
  pathways: getIcon('ic_pathways'),
  explore_2: getIcon('ic_explore_2')
};

export const learnSection = {
  subheader: 'menu.learn',
  items: [
    {
      title: 'menu.overview',
      path: PATH_DASHBOARD.overview.root,
      icon: <GridViewOutlinedIcon sx={{ width: '100%', height: '100%' }} />
    },
    {
      title: 'menu.flashcard_practice',
      path: PATH_DASHBOARD.flashcards.root,
      icon: <StyleIcon sx={{ width: '100%', height: '100%' }} />
    },
    {
      title: 'menu.topics',
      path: PATH_DASHBOARD.topics.root,
      icon: <ArticleOutlinedIcon sx={{ width: '100%', height: '100%' }} />
    },
    {
      title: 'menu.my_pathways',
      path: PATH_DASHBOARD.pathways.view,
      icon: ICONS.pathways
    }
  ]
};

function buildCircleItem(circle: SidebarCircle) {
  return {
    title: circle.name,
    path: `${PATH_DASHBOARD.circle.root}/${circle.guid}`,
    icon: ICONS.explore_2
  };
}

// Personal context aggregates personal circles + every org's circles you belong to.
// Rather than badge each org circle (which competes with the circle name for space
// in a fixed-height sidebar row), circles shared via an org get their own group,
// headed by that org's name — mirrors how buildSpaceManagementSection already uses
// a raw org name as a subheader.
export function buildPeersSections(circles: SidebarCircle[], currentUserGuid?: string) {
  const memberCircles = circles.filter((circle) => circle.adminId !== currentUserGuid);

  const personalItems = memberCircles
    .filter((circle) => !circle.organizationName)
    .map(buildCircleItem);

  const orgGroups = new Map<string, SidebarCircle[]>();
  memberCircles
    .filter((circle) => circle.organizationName)
    .forEach((circle) => {
      const orgName = circle.organizationName as string;
      const group = orgGroups.get(orgName);
      if (group) {
        group.push(circle);
      } else {
        orgGroups.set(orgName, [circle]);
      }
    });

  const sections: { subheader: string; items: ReturnType<typeof buildCircleItem>[] }[] = [];

  if (personalItems.length > 0) {
    sections.push({ subheader: 'menu.peers', items: personalItems });
  }

  orgGroups.forEach((groupCircles, orgName) => {
    sections.push({ subheader: orgName, items: groupCircles.map(buildCircleItem) });
  });

  return sections;
}

export function buildSpaceManagementSection(activeMembership?: OrganizationMembership) {
  if (!activeMembership || !isOrgAdmin(activeMembership.role)) return null;

  return {
    subheader: activeMembership.name,
    items: [
      {
        title: 'menu.overview',
        path: PATH_ORG.overview,
        icon: <BusinessOutlinedIcon sx={{ width: '100%', height: '100%' }} />
      },
      {
        title: 'menu.pathways',
        path: PATH_ORG.pathways,
        icon: ICONS.pathways
      },
      {
        title: 'menu.circles',
        path: PATH_ORG.circles,
        icon: <GroupsOutlinedIcon sx={{ width: '100%', height: '100%' }} />
      },
      {
        title: 'menu.members',
        path: PATH_ORG.members,
        icon: <PeopleOutlinedIcon sx={{ width: '100%', height: '100%' }} />
      }
    ]
  };
}

function buildNavConfig(
  circles: SidebarCircle[],
  currentUserGuid?: string,
  activeMembership?: OrganizationMembership
) {
  const spaceManagement = buildSpaceManagementSection(activeMembership);

  const sections = activeMembership ? [] : [learnSection];

  // Peers ("Shared with me") duplicates content already reachable via the Space's
  // own Circles/Pathways links once an org is active — only show it in Personal context.
  if (!activeMembership) {
    sections.push(...buildPeersSections(circles, currentUserGuid));
  }

  if (spaceManagement) sections.push(spaceManagement);
  return sections;
}

export default buildNavConfig;
